import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Row, Col, FormText } from 'reactstrap';
import { isNumber, Translate, translate, ValidatedField, ValidatedForm } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';
import { mapIdList } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { Hour } from 'app/shared/model/enumerations/hour.model';
import { Minute } from 'app/shared/model/enumerations/minute.model';
import { AMPM } from 'app/shared/model/enumerations/ampm.model';

import { IActivityAvailability } from 'app/shared/model/activity-availability.model';
import { getEntities as getActivityAvailabilities } from 'app/entities/activity-availability/activity-availability.reducer';
import { IActivityTimeAvailability } from 'app/shared/model/activity-time-availability.model';
import { getEntity, updateEntity, createEntity, reset } from './activity-time-availability.reducer';

export const ActivityTimeAvailabilityUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const activityAvailabilities = useAppSelector(state => state.activityAvailability.entities);
  const activityTimeAvailabilityEntity = useAppSelector(state => state.activityTimeAvailability.entity);
  const loading = useAppSelector(state => state.activityTimeAvailability.loading);
  const updating = useAppSelector(state => state.activityTimeAvailability.updating);
  const updateSuccess = useAppSelector(state => state.activityTimeAvailability.updateSuccess);
  const hourValues = Object.values(Hour);
  const minuteValues = Object.values(Minute);
  const aMPMValues = Object.keys(AMPM);

  const handleClose = () => {
    navigate('/activity-time-availability' + location.search);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getActivityAvailabilities({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    values.fromDate = convertDateTimeToServer(values.fromDate);
    values.toDate = convertDateTimeToServer(values.toDate);

    const entity = {
      ...activityTimeAvailabilityEntity,
      ...values,
      activityAvailability: activityAvailabilities.find(it => it.id.toString() === values.activityAvailability.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {
          fromDate: displayDefaultDateTime(),
          toDate: displayDefaultDateTime(),
        }
      : {
          hour: 'ONE',
          minute: 'ZERO',
          ampm: 'AM',
          ...activityTimeAvailabilityEntity,
          fromDate: convertDateTimeFromServer(activityTimeAvailabilityEntity.fromDate),
          toDate: convertDateTimeFromServer(activityTimeAvailabilityEntity.toDate),
          activityAvailability: activityTimeAvailabilityEntity?.activityAvailability?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="activitiesApp.activityTimeAvailability.home.createOrEditLabel" data-cy="ActivityTimeAvailabilityCreateUpdateHeading">
            <Translate contentKey="activitiesApp.activityTimeAvailability.home.createOrEditLabel">
              Create or edit a ActivityTimeAvailability
            </Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="activity-time-availability-id"
                  label={translate('global.field.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('activitiesApp.activityTimeAvailability.fromDate')}
                id="activity-time-availability-fromDate"
                name="fromDate"
                data-cy="fromDate"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('activitiesApp.activityTimeAvailability.toDate')}
                id="activity-time-availability-toDate"
                name="toDate"
                data-cy="toDate"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('activitiesApp.activityTimeAvailability.hour')}
                id="activity-time-availability-hour"
                name="hour"
                data-cy="hour"
                type="select"
              >
                {hourValues.map(hour => (
                  <option value={hour} key={hour}>
                    {'' + hour}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('activitiesApp.activityTimeAvailability.minute')}
                id="activity-time-availability-minute"
                name="minute"
                data-cy="minute"
                type="select"
              >
                {minuteValues.map(minute => (
                  <option value={minute} key={minute}>
                    {'' + minute}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('activitiesApp.activityTimeAvailability.ampm')}
                id="activity-time-availability-ampm"
                name="ampm"
                data-cy="ampm"
                type="select"
              >
                {aMPMValues.map(aMPM => (
                  <option value={aMPM} key={aMPM}>
                    {translate('activitiesApp.AMPM.' + aMPM)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('activitiesApp.activityTimeAvailability.adultCount')}
                id="activity-time-availability-adultCount"
                name="adultCount"
                data-cy="adultCount"
                type="text"
              />
              <ValidatedField
                label={translate('activitiesApp.activityTimeAvailability.childCount')}
                id="activity-time-availability-childCount"
                name="childCount"
                data-cy="childCount"
                type="text"
              />
              <ValidatedField
                label={translate('activitiesApp.activityTimeAvailability.infantCount')}
                id="activity-time-availability-infantCount"
                name="infantCount"
                data-cy="infantCount"
                type="text"
              />
              <ValidatedField
                label={translate('activitiesApp.activityTimeAvailability.userName')}
                id="activity-time-availability-userName"
                name="userName"
                data-cy="userName"
                type="text"
              />
              <ValidatedField
                id="activity-time-availability-activityAvailability"
                name="activityAvailability"
                data-cy="activityAvailability"
                label={translate('activitiesApp.activityTimeAvailability.activityAvailability')}
                type="select"
              >
                <option value="" key="0" />
                {activityAvailabilities
                  ? activityAvailabilities.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/activity-time-availability" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default ActivityTimeAvailabilityUpdate;
